import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import './ScreenShareHistory.css';

// Playback speeds
const SPEEDS = [1, 2, 4, 8, 16];

// LRU cache for decoded frames
const MAX_CACHE_SIZE = 200;

/**
 * ScreenShareHistory - DVR-style playback viewer
 *
 * Renders inside the video-container when viewMode === 'history'.
 * Shows captured screen frames with timeline scrubber, playback controls,
 * and activity heatmap.
 */
const ScreenShareHistory = ({ videoAspectRatio, nativeResolution }) => {
  const { featureManager } = useWebRTC();
  const canvasRef = useRef(null);
  const frameCacheRef = useRef(new Map()); // eventId → base64
  const cacheOrderRef = useRef([]); // LRU order

  // Frame index
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const playIntervalRef = useRef(null);

  // Activity map for heatmap
  const [activityMap, setActivityMap] = useState([]);

  // Time range
  const [timeRange, setTimeRange] = useState(() => {
    const now = Date.now();
    return { since: now - 60 * 60 * 1000, until: now }; // Last hour
  });

  // Zoom level for scrubber (minutes visible)
  const [zoomMinutes, setZoomMinutes] = useState(60);

  // Recorder status
  const [status, setStatus] = useState(null);

  const recorder = useMemo(() => {
    return featureManager?.getFeature('screen-recorder');
  }, [featureManager]);

  // ==================== Load Frame Index ====================

  const loadFrameIndex = useCallback(async () => {
    if (!recorder) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await recorder.requestFrameIndex(timeRange.since, timeRange.until);
      if (result?.success && result.frames) {
        setFrames(result.frames);
        // Jump to the last frame
        if (result.frames.length > 0) {
          setCurrentIndex(result.frames.length - 1);
        }
      }
    } catch (error) {
      console.error('[ScreenShareHistory] Failed to load frame index:', error);
    }
    setLoading(false);
  }, [recorder, timeRange]);

  const loadActivityMap = useCallback(async () => {
    if (!recorder) return;
    try {
      const result = await recorder.requestActivityMap(timeRange.since, timeRange.until);
      if (result?.success && result.minutes) {
        setActivityMap(result.minutes);
      }
    } catch (error) {
      console.error('[ScreenShareHistory] Failed to load activity map:', error);
    }
  }, [recorder, timeRange]);

  const loadStatus = useCallback(async () => {
    if (!recorder) return;
    try {
      const result = await recorder.requestStatus();
      if (result?.success) {
        setStatus(result);
      }
    } catch (error) {
      // Ignore status errors
    }
  }, [recorder]);

  // Load data on mount and when time range changes
  useEffect(() => {
    loadFrameIndex();
    loadActivityMap();
    loadStatus();
  }, [loadFrameIndex, loadActivityMap, loadStatus]);

  // Refresh frame index periodically when not playing
  useEffect(() => {
    if (isPlaying) return;
    const interval = setInterval(() => {
      loadFrameIndex();
      loadActivityMap();
    }, 10000);
    return () => clearInterval(interval);
  }, [isPlaying, loadFrameIndex, loadActivityMap]);

  // ==================== Frame Loading & Display ====================

  const loadAndDisplayFrame = useCallback(async (index) => {
    if (!recorder || index < 0 || index >= frames.length) return;

    const frame = frames[index];
    const cache = frameCacheRef.current;

    // Check cache
    let base64 = cache.get(frame.id);
    if (!base64) {
      try {
        const result = await recorder.requestFrame(frame.id);
        if (result?.success && result.imageData) {
          base64 = result.imageData;
          // Add to LRU cache
          cache.set(frame.id, base64);
          cacheOrderRef.current.push(frame.id);
          // Evict if over limit
          while (cacheOrderRef.current.length > MAX_CACHE_SIZE) {
            const evictId = cacheOrderRef.current.shift();
            cache.delete(evictId);
          }
        }
      } catch (error) {
        console.error('[ScreenShareHistory] Failed to load frame:', error);
        return;
      }
    }

    if (!base64) return;

    // Draw to canvas
    const canvas = canvasRef.current;
    if (!canvas) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
    };
    img.src = `data:image/jpeg;base64,${base64}`;
  }, [recorder, frames]);

  // Display current frame when index changes
  useEffect(() => {
    loadAndDisplayFrame(currentIndex);
  }, [currentIndex, loadAndDisplayFrame]);

  // Pre-fetch frames ahead and behind
  useEffect(() => {
    if (!recorder || frames.length === 0) return;

    const prefetchIds = [];
    // 10 ahead
    for (let i = 1; i <= 10; i++) {
      const idx = currentIndex + i;
      if (idx < frames.length && !frameCacheRef.current.has(frames[idx].id)) {
        prefetchIds.push(frames[idx].id);
      }
    }
    // 5 behind
    for (let i = 1; i <= 5; i++) {
      const idx = currentIndex - i;
      if (idx >= 0 && !frameCacheRef.current.has(frames[idx].id)) {
        prefetchIds.push(frames[idx].id);
      }
    }

    if (prefetchIds.length > 0) {
      recorder.requestFramesBatch(prefetchIds).then(result => {
        if (result?.success && result.frames) {
          const cache = frameCacheRef.current;
          for (const [id, data] of Object.entries(result.frames)) {
            if (!cache.has(id)) {
              cache.set(id, data);
              cacheOrderRef.current.push(id);
            }
          }
          // Evict excess
          while (cacheOrderRef.current.length > MAX_CACHE_SIZE) {
            const evictId = cacheOrderRef.current.shift();
            cache.delete(evictId);
          }
        }
      }).catch(err => console.warn('[ScreenShareHistory] Failed to fetch frame:', err.message));
    }
  }, [currentIndex, frames, recorder]);

  // ==================== Playback ====================

  useEffect(() => {
    if (isPlaying) {
      const intervalMs = Math.max(100, 1000 / speed);
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= frames.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, intervalMs);
    } else {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    }
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, [isPlaying, speed, frames.length]);

  const togglePlayback = useCallback(() => {
    if (currentIndex >= frames.length - 1 && !isPlaying) {
      // Restart from beginning if at end
      setCurrentIndex(0);
    }
    setIsPlaying(prev => !prev);
  }, [currentIndex, frames.length, isPlaying]);

  const stepFrame = useCallback((delta) => {
    setCurrentIndex(prev => Math.max(0, Math.min(frames.length - 1, prev + delta)));
    setIsPlaying(false);
  }, [frames.length]);

  const jumpToStart = useCallback(() => {
    setCurrentIndex(0);
    setIsPlaying(false);
  }, []);

  const jumpToEnd = useCallback(() => {
    setCurrentIndex(Math.max(0, frames.length - 1));
    setIsPlaying(false);
  }, [frames.length]);

  // ==================== Scrubber Interaction ====================

  const handleScrubberClick = useCallback((e) => {
    if (frames.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const index = Math.round(pct * (frames.length - 1));
    setCurrentIndex(index);
    setIsPlaying(false);
  }, [frames.length]);

  const handleMinimapClick = useCallback((e) => {
    // Click on minimap to jump to that time
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const now = Date.now();
    const dayStart = now - 24 * 60 * 60 * 1000;
    const clickTime = dayStart + pct * 24 * 60 * 60 * 1000;

    // Center the zoom window on click time
    const halfRange = (zoomMinutes * 60 * 1000) / 2;
    setTimeRange({
      since: Math.max(dayStart, clickTime - halfRange),
      until: Math.min(now, clickTime + halfRange)
    });
  }, [zoomMinutes]);

  const zoomIn = useCallback(() => {
    setZoomMinutes(prev => Math.max(5, Math.round(prev / 2)));
    // Recalculate time range centered on current frame
    if (frames.length > 0 && frames[currentIndex]) {
      const center = frames[currentIndex].timestamp;
      const halfMs = (Math.max(5, Math.round(zoomMinutes / 2)) * 60 * 1000) / 2;
      setTimeRange({ since: center - halfMs, until: center + halfMs });
    }
  }, [zoomMinutes, frames, currentIndex]);

  const zoomOut = useCallback(() => {
    setZoomMinutes(prev => Math.min(1440, prev * 2));
    if (frames.length > 0 && frames[currentIndex]) {
      const center = frames[currentIndex].timestamp;
      const halfMs = (Math.min(1440, zoomMinutes * 2) * 60 * 1000) / 2;
      setTimeRange({ since: center - halfMs, until: center + halfMs });
    }
  }, [zoomMinutes, frames, currentIndex]);

  // ==================== Helpers ====================

  const formatTime = useCallback((ts) => {
    if (!ts) return '--:--:--';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour12: false });
  }, []);

  const currentFrame = frames[currentIndex];
  const playheadPct = frames.length > 1 ? (currentIndex / (frames.length - 1)) * 100 : 0;

  // Build heatmap bars from activity map
  const heatBars = useMemo(() => {
    if (activityMap.length === 0) return [];
    const maxCount = Math.max(1, ...activityMap.map(m => m.count));
    return activityMap.map(m => ({
      timestamp: m.timestamp,
      height: Math.max(2, (m.count / maxCount) * 100),
      high: m.count > maxCount * 0.7
    }));
  }, [activityMap]);

  // Minimap density (24h, grouped into 5-minute buckets)
  const minimapBars = useMemo(() => {
    const now = Date.now();
    const dayStart = now - 24 * 60 * 60 * 1000;
    const bucketMs = 5 * 60 * 1000;
    const bucketCount = Math.ceil(24 * 60 / 5);
    const buckets = new Array(bucketCount).fill(0);

    for (const m of activityMap) {
      const bucketIndex = Math.floor((m.timestamp - dayStart) / bucketMs);
      if (bucketIndex >= 0 && bucketIndex < bucketCount) {
        buckets[bucketIndex] += m.count;
      }
    }

    const maxBucket = Math.max(1, ...buckets);
    return buckets.map(count => {
      const ratio = count / maxBucket;
      if (count === 0) return 'idle';
      if (ratio > 0.7) return 'flagged';
      if (ratio > 0.3) return 'normal';
      return 'idle';
    });
  }, [activityMap]);

  // Minimap viewport bracket position
  const minimapBracket = useMemo(() => {
    const now = Date.now();
    const dayStart = now - 24 * 60 * 60 * 1000;
    const dayMs = 24 * 60 * 60 * 1000;
    const left = Math.max(0, ((timeRange.since - dayStart) / dayMs) * 100);
    const right = Math.min(100, ((timeRange.until - dayStart) / dayMs) * 100);
    return { left: `${left}%`, width: `${Math.max(1, right - left)}%` };
  }, [timeRange]);

  // ==================== Render ====================

  if (loading && frames.length === 0) {
    return (
      <div className="history-viewer">
        <div className="history-loading">
          <div className="spinner" />
          Loading history...
        </div>
      </div>
    );
  }

  return (
    <div className="history-viewer">
      {/* Frame Viewport */}
      <div className="history-viewport" style={{ aspectRatio: videoAspectRatio }}>
        {frames.length === 0 ? (
          <div className="no-frames">
            <div className="no-frames-icon">&#128249;</div>
            <p>No recorded frames available</p>
            <small>Frames are captured when screen sharing is active</small>
          </div>
        ) : (
          <canvas ref={canvasRef} />
        )}

        {/* Bottom Control Bar */}
        {frames.length > 0 && (
          <div className={`history-controls ${isPlaying ? 'always-visible' : ''}`}>
            {/* Info row */}
            <div className="controls-info-row">
              <span className="controls-timestamp">
                {formatTime(currentFrame?.timestamp)}
              </span>
              <span className="playback-badge">
                <span className="rec-dot" />
                PLAYBACK
              </span>
            </div>

            {/* Mini scrubber */}
            <div className="mini-scrubber" onClick={handleScrubberClick}>
              <div className="mini-scrubber-track" />
              <div className="mini-scrubber-heatmap">
                {heatBars.map((bar, i) => (
                  <div
                    key={i}
                    className="heat-bar"
                    style={{ height: `${bar.height}%` }}
                  />
                ))}
              </div>
              <div
                className="mini-scrubber-playhead"
                style={{ left: `${playheadPct}%` }}
              />
            </div>

            {/* Action row */}
            <div className="controls-action-row">
              <div className="playback-buttons">
                <button onClick={jumpToStart} data-tooltip="Jump to start">&#9198;</button>
                <button onClick={() => stepFrame(-1)} data-tooltip="Previous frame">&#9194;</button>
                <button className="play-pause" onClick={togglePlayback} data-tooltip={isPlaying ? 'Pause' : 'Play'}>
                  {isPlaying ? '\u23F8' : '\u25B6'}
                </button>
                <button onClick={() => stepFrame(1)} data-tooltip="Next frame">&#9193;</button>
                <button onClick={jumpToEnd} data-tooltip="Jump to end">&#9197;</button>
              </div>

              <div className="speed-selector">
                {SPEEDS.map(s => (
                  <button
                    key={s}
                    className={speed === s ? 'active' : ''}
                    onClick={() => setSpeed(s)}
                  >
                    {s}&times;
                  </button>
                ))}
              </div>

              <div className="flag-nav">
                <button data-tooltip="Previous flag">&larr; PREV</button>
                <button data-tooltip="Next flag">NEXT &rarr;</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full Scrubber */}
      {frames.length > 0 && (
        <div className="history-scrubber">
          <div className="scrubber-header">
            <div className="scrubber-time-labels">
              <span>{formatTime(timeRange.since)}</span>
              <span>{formatTime(timeRange.until)}</span>
            </div>
            <div className="scrubber-zoom-controls">
              <button onClick={zoomIn} data-tooltip="Zoom in">+</button>
              <span className="scrubber-zoom-label">{zoomMinutes}m</span>
              <button onClick={zoomOut} data-tooltip="Zoom out">&minus;</button>
            </div>
          </div>

          <div className="scrubber-track" onClick={handleScrubberClick}>
            <div className="scrubber-heatmap">
              {heatBars.map((bar, i) => (
                <div
                  key={i}
                  className={`heat-bar ${bar.high ? 'high' : ''}`}
                  style={{ height: `${bar.height}%` }}
                />
              ))}
            </div>
            <div
              className="scrubber-playhead"
              style={{ left: `${playheadPct}%` }}
            />
          </div>
        </div>
      )}

      {/* 24h Minimap */}
      <div className="history-minimap">
        <div className="minimap-label">24h overview</div>
        <div className="minimap-track" onClick={handleMinimapClick}>
          <div className="minimap-bars">
            {minimapBars.map((type, i) => (
              <div key={i} className={`minimap-bar ${type}`} />
            ))}
          </div>
          <div
            className="minimap-viewport-bracket"
            style={minimapBracket}
          />
        </div>
      </div>
    </div>
  );
};

export default ScreenShareHistory;
